/**
 * Immutable deep-set helper.
 *
 * ``setIn(obj, ["professional", "job_title"], "CEO")`` returns a new object
 * with that path written. Missing intermediate objects are created; existing
 * ones are shallow-copied. Arrays are not supported as path segments — all
 * segments are treated as plain object keys (our JSONB shape uses objects
 * throughout).
 */
export function setIn<T extends Record<string, unknown>>(
  obj: T,
  path: readonly string[],
  value: unknown,
): T {
  if (path.length === 0) return obj;
  const [head, ...rest] = path;
  const current = (obj as Record<string, unknown>)[head];
  const nextChild =
    rest.length === 0
      ? value
      : setIn(
          (current && typeof current === "object" ? current : {}) as Record<string, unknown>,
          rest,
          value,
        );
  return { ...(obj as Record<string, unknown>), [head]: nextChild } as T;
}
